// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */
import { ApolloLink, Observable } from 'apollo-link';

import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { getLogger } from '@confluence/logger';
import {
	BadStatusError,
	isSessionExpiredError,
	NoNetworkError,
	SessionExpiredError,
	causedByAbortError,
} from '@confluence/network';
import {
	ClientNetworkStatus,
	getNetworkStatus,
	setNetworkStatus,
} from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache.ts';

export function writeNetworkStatus(status: ClientNetworkStatus): void {
	const currentStatus = getNetworkStatus();
	if (currentStatus === ClientNetworkStatus.SESSION_EXPIRED) {
		// recovering from this error only via page reload
		return;
	}

	if (currentStatus !== status) {
		setNetworkStatus(status);
	}
}

export const networkStatusLink = (): ApolloLink => {
	const logger = getLogger(networkStatusLink);

	return new ApolloLink((operation, forward) => {
		return new Observable((observer) => {
			forward(operation).subscribe({
				next(result) {
					const context = operation.getContext();

					if (result.errors?.some(isSessionExpiredError)) {
						writeNetworkStatus(ClientNetworkStatus.SESSION_EXPIRED);

						const sessionExpiredError = new SessionExpiredError(
							operation.operationName,
							context.response || {},
						);
						// Don't throw original errors when translating them:
						// 1. By Error's convention, the original cause of the Error is in
						//    `cause`.
						// 2. By our convention inspired by GraphQLError, the original cause
						//    of the Error is in `originalError`).
						(sessionExpiredError as any).originalError = sessionExpiredError.cause =
							new ApolloErrorInterop({
								graphQLErrors: result.errors,
							});
						observer.error(sessionExpiredError);
					} else {
						writeNetworkStatus(ClientNetworkStatus.ONLINE);
						observer.next(result);
					}
				},

				error(error) {
					if (error instanceof BadStatusError) {
						switch (error.response.status) {
							case /* Forbidden */ 403:
								logger.log`IP blocked for ${operation}`;
								writeNetworkStatus(ClientNetworkStatus.IP_BLOCKED);
								return; // as it doesn't call observer next/error, the request will stay in unresolved state

							case /* Too Many Requests */ 429:
								logger.log`Rate limit was reached for ${operation}`;
								writeNetworkStatus(ClientNetworkStatus.RATE_LIMITED);
								observer.error(error);
								return;
						}
					} else if (
						error instanceof NoNetworkError &&
						!error.ignore &&
						!causedByAbortError(error)
					) {
						logger.log`Network is offline for ${operation}`;
						writeNetworkStatus(ClientNetworkStatus.OFFLINE);
						return; // as it doesn't call observer next/error, the request will stay in unresolved state;
					}

					observer.error(error);
				},

				complete() {
					observer.complete();
				},
			});
		});
	});
};
